import { Fragment, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import MiniCalendarPicker from '../components/MiniCalendarPicker';
import { PortalPickerDropdown } from '../components/PortalPickerDropdown';
import ItemPickerSheet from '../components/ItemPickerSheet';
import {
  IonContent,
  IonPage,
  IonButton,
  IonIcon,
  IonSpinner,
  IonGrid,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonAvatar,
  IonRadioGroup,
  IonRadio,
  IonModal,
  useIonToast,
} from '@ionic/react';
import {
  closeOutline,
  chevronDownOutline,
  trashOutline,
  searchOutline,
  cubeOutline,
  createOutline,
  checkmarkOutline,
  ellipsisVertical,
  shareOutline,
  documentOutline,
  documentTextOutline,
  reloadOutline,
  personOutline,
  mailOutline,
  cashOutline,
  checkmarkCircle,
} from 'ionicons/icons';
import { doc, getDoc } from 'firebase/firestore';
import { dbContacts } from '../auth/firebase';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import type { ContactListItem, ContactAddress } from '@rsmaids/shared/types/contacts';
import { getCachedContactList, getCachedContact } from '@/utils/contactCache';
import { uniqueQuoteAddresses, formatQuoteAddress, type QuoteAddress } from '@/utils/quoteAddress';
import {
  createInvoice, updateInvoice, getInvoices, getQuotes, getSalesOrders, makeZohoInvoiceRecurring,
  mapInvoiceWithOrder, updateContact as updateZohoContact,
} from '../services/zohoInvoicingService';
import { saveInvoiceMetadata } from '../services/invoiceMetadataService';
import type { ZohoInvoice, ZohoContact, ZohoLineItem } from '../services/zohoInvoicingService';
import { fetchZohoAll, fetchZohoItems, fetchZohoTaxes, fetchZohoPricelists, fetchZohoPricelistItems, fetchZohoServiceMonths, type ZohoItem, type ZohoTax, type ZohoPricelist } from '../services/zohoServiceOrderService';
import { getPricelistInfo, zipToPricelistCode } from '@/utils/pricelists';
import EmailCompose from './EmailCompose';
import styles from './CreateInvoice.module.scss';
import BasePageWithHeader from '../components/BasePageWithHeader';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { dfLocale } from '../i18n/locale';

// ─── Types ────────────────────────────────────────────────────────────────────
interface LineItem {
  id: number;
  description: string;       // item name
  itemDescription: string;   // separate description text (Service Order only)
  quantity: number;
  price: number;
  discountAmount: string;
  discountType: '%' | '$';
  taxRate: string;   // INVOICE/QUOTE: fraction string (e.g. '0.104')
  item_id?: string;  // Zoho catalog item_id (ORDER type)
  tax_id?: string;   // Zoho tax_id (ORDER type)
}

interface CreateInvoicePrefillLineItem {
  // Becomes the invoice line's item name (Zoho field `name`) — when linking
  // back to a sales order (linkedSalesOrderNumber below), this needs to be
  // the item's exact name with nothing appended, since Zoho's mapwithorder
  // association requires an exact item-name match against the sales order's
  // own line items.
  description: string;
  // Becomes the invoice line's separate description field — doesn't affect
  // the name-matching requirement above.
  itemDescription?: string;
  quantity: number;
  price: number;
  discountAmount?: string;
  taxRate?: string;
}

interface CreateInvoicePrefill {
  // Seeds the search box for a manual pick.
  customerName?: string;
  // Zoho contact_id — when the caller already has it (e.g. the sales order's
  // own customer_id), this auto-selects the matching contact via
  // pickCustomer() once the real contact list loads (see the effect below),
  // getting its real avatar in the process. Matching by ID, not customerName
  // text, since two different customers can share the same display name.
  contactId?: string;
  // The source sales order's number (e.g. "SO-010633") and Zoho ID — sets
  // the invoice's real "Order Number" field (Zoho's API name for it is
  // salesorder_number, confirmed against ZohoInvoice's own type in
  // zohoInvoicingService.ts — NOT reference_number, a different, unrelated
  // field). Setting it is one of Zoho's own documented prerequisites for
  // associating an invoice with a sales order (see help.zoho.com's
  // "Associate Invoice With Sales Order" article) — customer name and line
  // item names must also match, which the rest of this prefill already
  // carries over. The actual association (and closing the SO) still needs a
  // follow-up POST /invoices/mapwithorder call after the invoice is created
  // — this field alone only satisfies the "Order Number" prerequisite.
  linkedSalesOrderNumber?: string;
  lineItems: CreateInvoicePrefillLineItem[];
  // Opens the form on this document type instead of the default ORDER — e.g. the
  // Price Calculator prefills a QUOTE from its estimate, with no customer picked yet.
  invoiceType?: 'ORDER' | 'INVOICE' | 'QUOTE';
}

// Opens the form pre-loaded with an existing Zoho INVOICE for editing — unlike
// `prefill` (which seeds a brand-new record), this switches the whole form into
// edit mode: the type-switcher locks to INVOICE, the invoice number isn't
// auto-generated, and submitting calls Zoho's update endpoint instead of create.
interface CreateInvoiceEdit {
  invoice: ZohoInvoice;
  // Editing a quote (Zoho estimate): opens as QUOTE and saves to the estimates endpoint.
  isEstimate?: boolean;
  metadata?: { serviceMonth?: string };
}

interface CreateInvoiceProps {
  onClose?: () => void;
  onCreated?: (invoice: ZohoInvoice) => void;
  // Optional prefill — used when creating an invoice from an existing Zoho Sales/Service
  // Order (see BookingDetails.tsx). Seeds the customer search box and line items instead
  // of leaving the form blank; the customer still must be picked from search results
  // (we don't fabricate a ZohoContact record with a guessed contact_id).
  prefill?: CreateInvoicePrefill;
  edit?: CreateInvoiceEdit;
}

// ─── Constants ────────────────────────────────────────────────────────────────
/** Zoho tax matching a percentage (e.g. 10.55); tax groups win over single taxes. */
function findTaxIdByPercent(taxes: ZohoTax[], pct: number): string | undefined {
  const hits = taxes.filter(t => Math.abs(t.tax_percentage - pct) < 0.01);
  return (hits.find(t => t.is_tax_group) ?? hits[0])?.tax_id;
}

/** A line's Zoho tax_id; legacy prefilled lines carry only a fractional taxRate. */
function resolveLineTaxId(l: { tax_id?: string; taxRate: string }, taxes: ZohoTax[]): string | undefined {
  if (l.tax_id) return l.tax_id;
  const r = parseFloat(l.taxRate);
  return r > 0 ? findTaxIdByPercent(taxes, r * 100) : undefined;
}

/** Individual taxes first, then tax groups (Zoho's own order), so the dropdown shows two sections. */
function sortTaxesForPicker(taxes: ZohoTax[]): ZohoTax[] {
  return [...taxes.filter(t => !t.is_tax_group), ...taxes.filter(t => t.is_tax_group)];
}

const TYPE_OPTIONS = [
  { value: 'ORDER', get label() { return i18n.t('pages.CreateInvoice.serviceOrder'); } },
  { value: 'INVOICE', get label() { return i18n.t('pages.CreateInvoice.invoice'); } },
  { value: 'QUOTE', get label() { return i18n.t('pages.CreateInvoice.quote'); } },
];

const SO_PAYMENT_TERMS = [
  { get label() { return i18n.t('pages.CreateInvoice.dueOnReceipt'); }, days: 0 },
  { get label() { return i18n.t('pages.CreateInvoice.net3'); },  days: 3 },
  { get label() { return i18n.t('pages.CreateInvoice.net7'); },  days: 7 },
  { get label() { return i18n.t('pages.CreateInvoice.net15'); }, days: 15 },
  { get label() { return i18n.t('pages.CreateInvoice.net30'); }, days: 30 },
  { get label() { return i18n.t('pages.CreateInvoice.net45'); }, days: 45 },
  { get label() { return i18n.t('pages.CreateInvoice.net60'); }, days: 60 },
];

let _nextId = 1;
const nextId = () => _nextId++;

const emptyLine = (): LineItem => ({
  id: nextId(),
  description: '',
  itemDescription: '',
  quantity: 1,
  price: 0,
  discountAmount: '',
  discountType: '$',
  taxRate: '',
});

// Zoho's line-item `discount` field is a plain number (dollar amount) or a
// string like "10%" (percentage) — used only when loading an existing
// invoice into edit mode, since new lines are always built from form input.
function parseLineDiscount(raw: unknown): { discountAmount: string; discountType: '%' | '$' } {
  if (raw == null || raw === '' || raw === 0) return { discountAmount: '', discountType: '$' };
  if (typeof raw === 'string' && raw.trim().endsWith('%')) {
    return { discountAmount: raw.trim().replace('%', ''), discountType: '%' };
  }
  return { discountAmount: String(raw), discountType: '$' };
}

function zohoLineItemsToFormLines(items: ZohoLineItem[] | undefined): LineItem[] {
  if (!items?.length) return [emptyLine()];
  return items.map(li => ({
    id: nextId(),
    ...(li.item_id ? { item_id: li.item_id } : {}),
    description: li.name ?? '',
    itemDescription: li.description ?? '',
    quantity: li.quantity ?? 1,
    price: li.rate ?? 0,
    ...parseLineDiscount(li['discount']),
    taxRate: '',
  }));
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

// Keep '.' out of the strip set — dropping it turns "1.5 Bath" into two separate word tokens "1" and "5", which then
// falsely satisfies an exact-word search for "5" on every 1.5-bath item.
const normalizeSearch = (s: string) => s.toLowerCase().replace(/[^a-z0-9.]/g, ' ').replace(/\s+/g, ' ').trim();

// Plain substring matching lets a lone digit like "5" match inside any unrelated number (500 sqft, 675 sqft, a $185
// price) — numeric tokens must match a whole word; text tokens still prefix-match so partial typing ("recur") works.
const itemMatchesQuery = (item: ZohoItem, query: string) => {
  const tokens = normalizeSearch(query).split(' ').filter(Boolean);
  if (!tokens.length) return true;
  const hayWords = normalizeSearch(item.name + ' ' + (item.description ?? '')).split(' ').filter(Boolean);
  return tokens.every(t => /^[0-9]+$/.test(t) ? hayWords.includes(t) : hayWords.some(w => w.startsWith(t)));
};

const autoIncrement = (num: string) => {
  const match = num.match(/^([A-Za-z-]*)([0-9]+)$/);
  if (!match) return num;
  return `${match[1]}${String(parseInt(match[2], 10) + 1).padStart(match[2].length, '0')}`;
};

// ─── Bottom Sheet ─────────────────────────────────────────────────────────────
// Still used for the few flows that don't have a desktop-inline equivalent:
// "more options" and recurring billing.
const Sheet: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
  title, onClose, children,
}) => (
  <IonLabel className={styles.sheetOverlay} onClick={onClose}>
    <IonLabel className={styles.sheet} onClick={e => e.stopPropagation()}>
      <IonLabel className={styles.sheetHeader}>
        <IonButton fill="clear" className={styles.sheetClose} onClick={onClose}>
          <IonIcon icon={closeOutline} />
        </IonButton>
        <span className={styles.sheetTitle}>{title}</span>
        <span style={{ width: 36 }} />
      </IonLabel>
      <IonLabel className={styles.sheetBody}>{children}</IonLabel>
    </IonLabel>
  </IonLabel>
);

// ─── Select field ─────────────────────────────────────────────────────────────
// Replaces native <select> everywhere in this form with the same PickerDropdown
// overlay used for customer search / BlockedTimeForm's staff picker, so every
// dropdown in the app renders with the same glass-panel chrome instead of the
// browser's own native picker UI.
function SelectField({
  value, options, onChange, placeholder, disabled, portalRef, className,
}: {
  value: string;
  // Optional per-option description — e.g. the pricelist zip/city coverage detail
  // desktop shows as a hover tooltip. Touch has no hover, so mobile shows it inline
  // under the option's name instead.
  // Optional group — e.g. distinguishing individual Zoho taxes from tax groups,
  // which otherwise render as one flat, unlabeled list with no way to tell them
  // apart. A header renders once, right before the first option of each group.
  options: { value: string; label: string; description?: string; group?: string }[];
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  portalRef: React.RefObject<HTMLElement | null>;
  className?: string;
}) {
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLIonItemElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const selected = options.find(o => o.value === value);

  return (
    <>
      <IonItem
        ref={fieldRef}
        button
        detail={false}
        lines="none"
        disabled={disabled}
        className={`${styles.selectField} ${className ?? ''}`}
        onClick={() => setOpen(v => !v)}
      >
        <IonLabel className={`${styles.selectFieldValue} ${!selected ? styles.selectPlaceholder : ''}`}>
          {selected?.label ?? placeholder ?? tr('pages.CreateInvoice.select')}
        </IonLabel>
        <IonIcon icon={chevronDownOutline} slot="end"
          className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ''}`} />
      </IonItem>
      <PortalPickerDropdown isOpen={open} fieldRef={fieldRef} portalRef={portalRef} close={close}
        align="left" matchWidth exactWidth className={styles.calendarDropdown}>
        <IonList lines="none" className={styles.dropdownList}>
          {options.map((o, i) => (
            <Fragment key={o.value}>
              {o.group && o.group !== options[i - 1]?.group && (
                <IonLabel className={styles.dropdownGroupLabel}>{o.group}</IonLabel>
              )}
              <IonItem button detail={false} lines="none"
                className={`${styles.dropdownItem}${o.value === value ? ` ${styles.dropdownItemActive}` : ''}`}
                onClick={() => { onChange(o.value); setOpen(false); }}>
                <IonLabel>
                  <h3>{o.label}</h3>
                  {o.description && <p className={styles.dropdownItemTip}>{o.description}</p>}
                </IonLabel>
              </IonItem>
            </Fragment>
          ))}
        </IonList>
      </PortalPickerDropdown>
    </>
  );
}

// ─── Date field ───────────────────────────────────────────────────────────────
// Same trigger + PickerDropdown pattern as SelectField, but opens BlockedTimeForm's
// shared MiniCalendarPicker instead of a plain option list — replaces native
// <input type="date"> everywhere in this form.
function DateField({
  value, onChange, portalRef, placeholder = i18n.t('pages.CreateInvoice.selectDate'),
}: {
  value: string; // YYYY-MM-DD
  onChange: (v: string) => void;
  portalRef: React.RefObject<HTMLElement | null>;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLIonItemElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const dateObj = value ? new Date(`${value}T00:00:00`) : null;

  return (
    <>
      <IonItem ref={fieldRef} button detail={false} lines="none" className={styles.selectField} onClick={() => setOpen(v => !v)}>
        <IonLabel className={`${styles.selectFieldValue} ${!dateObj ? styles.selectPlaceholder : ''}`}>
          {dateObj ? format(dateObj, 'EEE, MMM d, yyyy', { locale: dfLocale() }) : placeholder}
        </IonLabel>
        <IonIcon icon={chevronDownOutline} slot="end"
          className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ''}`} />
      </IonItem>
      <PortalPickerDropdown isOpen={open} fieldRef={fieldRef} portalRef={portalRef} close={close}
        align="left" className={styles.calendarDropdown}>
        <MiniCalendarPicker
          desktop
          value={dateObj}
          onSelect={(d) => { onChange(format(d, 'yyyy-MM-dd')); setOpen(false); }}
        />
      </PortalPickerDropdown>
    </>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
const CreateInvoice: React.FC<CreateInvoiceProps> = ({ onClose, onCreated, prefill, edit }) => {
  const { t: tr } = useTranslation();
  const [presentToast] = useIonToast();
  const successAlertShownRef = useRef(false);
  // ── Invoice details state — `edit` forces INVOICE type (only invoices can be
  // ── edited today) and seeds every field from the existing Zoho record. ──────
  const [invoiceType, setInvoiceType] = useState<string>(edit ? (edit.isEstimate ? 'QUOTE' : 'INVOICE') : (prefill?.invoiceType ?? 'ORDER'));
  const isQuote = invoiceType === 'QUOTE';
  const isServiceOrder = invoiceType === 'ORDER';
  const [invoiceNumber, setInvoiceNumber] = useState(
    (edit?.isEstimate ? edit.invoice.estimate_number : undefined) ?? edit?.invoice.invoice_number ?? ''
  );
  // Invoices: the number shown is only a preview — Zoho assigns the real one (its own
  // auto-numbering, which handles late-fee invoices etc.) unless the user types an override.
  const [invoiceNumberManual, setInvoiceNumberManual] = useState(false);
  const [issuedOn, setIssuedOn] = useState(() => edit?.invoice.date ?? new Date().toISOString().split('T')[0]);
  // A quote's "due date" is its expiry date (Zoho estimates have expiry_date, not due_date).
  const [dueDate, setDueDate] = useState(() =>
    (edit?.isEstimate ? edit.invoice.expiry_date : edit?.invoice.due_date)
      || new Date(Date.now() + (edit?.isEstimate ? 30 : 3) * 86400000).toISOString().split('T')[0]
  );
  const [referenceNumber, setReferenceNumber] = useState(edit?.invoice.reference_number ?? '');
  // Not user-editable — silently carried from Convert to Invoice's prefill
  // straight into the create payload as salesorder_number, Zoho's actual
  // "Order Number" field (see CreateInvoicePrefill's doc comment).
  const [linkedSalesOrderNumber, setLinkedSalesOrderNumber] = useState('');
  // Service month (INVOICE only) — a real Zoho custom field on the Invoice module,
  // same idea as Sales Order's own Service Month field below. Prefer the value
  // already on the Zoho invoice being edited over the older Firestore-only copy
  // (pre-custom-field invoices only ever had the Firestore value).
  const [serviceMonth, setServiceMonth] = useState(
    edit?.invoice.custom_fields?.find(f => f.label.toLowerCase() === 'service month')?.value
      ?? edit?.metadata?.serviceMonth
      ?? ''
  );
  const [invoiceServiceMonths, setInvoiceServiceMonths] = useState<string[]>([]);
  const [invoiceServiceMonthsLoading, setInvoiceServiceMonthsLoading] = useState(false);
  const invoiceServiceMonthCfIdRef = useRef('');
  const invoiceServiceMonthsFetchedRef = useRef(false);
  // Not a user-facing choice — Zoho's own invoice UI doesn't expose currency
  // as an editable field (it follows the customer's assigned currency). We
  // still send it, since Helcim<->Zoho reconciliation needs it on the backend.
  // When editing an existing invoice, keep whatever currency it already has.
  const currency: 'USD' | 'CAD' = (edit?.invoice.currency_code as 'USD' | 'CAD') ?? 'USD';
  const [terms, setTerms] = useState(''); // Terms & Conditions (Service Order only)
  const [comments, setComments] = useState(edit?.invoice.notes ?? '');

  // ── Service Order specific state ───────────────────────────────────────────
  const [paymentTermsDays, setPaymentTermsDays] = useState(3);
  const [isInclusiveTax, setIsInclusiveTax] = useState(true);
  const [soServiceMonth, setSoServiceMonth] = useState('');
  const [soServiceMonths, setSoServiceMonths] = useState<string[]>([]);
  const soServiceMonthCfIdRef = useRef('');
  const [adjustment, setAdjustment] = useState('');
  const [adjustmentName, setAdjustmentName] = useState('');

  // Auto-generate the invoice/quote/SO number whenever the document type changes —
  // skipped entirely in edit mode, which already has the real number from Zoho and
  // must never overwrite it (invoiceType is locked to INVOICE there anyway).
  // Clearing the number is computed during render (React's "adjust state
  // when a dependency changes" pattern) rather than as a synchronous
  // setState call at the top of the effect, which now only handles the
  // actual async fetch.
  const [prevInvoiceTypeForNumber, setPrevInvoiceTypeForNumber] = useState(invoiceType);
  if (!edit && invoiceType !== prevInvoiceTypeForNumber) {
    setPrevInvoiceTypeForNumber(invoiceType);
    setInvoiceNumber('');
    setInvoiceNumberManual(false);
  }
  useEffect(() => {
    if (edit) return;
    if (invoiceType === 'QUOTE') {
      getQuotes()
        .then(quotes => {
          if (!quotes.length) { setInvoiceNumber('QT-000001'); return; }
          setInvoiceNumber(autoIncrement(quotes[0].estimate_number ?? 'QT-000000'));
        })
        .catch(() => {/* leave blank if fetch fails */});
    } else if (invoiceType === 'ORDER') {
      getSalesOrders()
        .then(orders => {
          if (!orders.length) return;
          let maxNum = 0;
          let prefix = 'SO-';
          let padLen = 6;
          for (const o of orders) {
            const num = o.salesorder_number ?? '';
            const match = num.match(/^([A-Za-z-]+)([0-9]+)$/);
            if (!match) continue;
            const n = parseInt(match[2], 10);
            if (n > maxNum) { maxNum = n; prefix = match[1]; padLen = match[2].length; }
          }
          if (maxNum > 0) setInvoiceNumber(`${prefix}${String(maxNum + 1).padStart(padLen, '0')}`);
        })
        .catch(() => {/* leave blank if fetch fails */});
    } else {
      getInvoices()
        .then(invoices => {
          // Don't trust invoices[0]: the newest invoice can be a late-fee (or other
          // specially-numbered) one. Use the highest number within the most common
          // "PREFIX-digits" series, which is the regular invoice sequence.
          const series = new Map<string, { count: number; maxNum: number; padLen: number }>();
          for (const inv of invoices) {
            const m = (inv.invoice_number ?? '').match(/^([A-Za-z-]*)([0-9]+)$/);
            if (!m) continue;
            const n = parseInt(m[2], 10);
            const cur = series.get(m[1]) ?? { count: 0, maxNum: 0, padLen: m[2].length };
            cur.count++;
            if (n > cur.maxNum) { cur.maxNum = n; cur.padLen = m[2].length; }
            series.set(m[1], cur);
          }
          let best: [string, { count: number; maxNum: number; padLen: number }] | null = null;
          for (const e of series) if (!best || e[1].count > best[1].count) best = e;
          if (best) setInvoiceNumber(`${best[0]}${String(best[1].maxNum + 1).padStart(best[1].padLen, '0')}`);
        })
        .catch(() => {/* leave blank if fetch fails */});
    }
  }, [invoiceType, edit]);

  // ── Recurring billing state (Invoice type only) ────────────────────────────
  const [recurBillingPeriod, setRecurBillingPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('monthly');
  const [recurIncrements, setRecurIncrements] = useState(1);
  const [recurCycles, setRecurCycles] = useState(0);           // 0 = infinite
  const [recurStartDate, setRecurStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [isSubmittingRecurring, setIsSubmittingRecurring] = useState(false);
  const [recurringError, setRecurringError] = useState<string | null>(null);
  const [recurringSuccess, setRecurringSuccess] = useState(false);

  // ── Customer state ────────────────────────────────────────────────────────
  // Searches the same local cached contact list desktop uses (DataServiceFactory),
  // not a live Zoho lookup — Zoho contacts carry no photo, and this list is
  // already filterable client-side with zero network round-trip.
  const [selectedCustomer, setSelectedCustomer] = useState<ZohoContact | null>(() =>
    edit ? { contact_id: edit.invoice.customer_id, contact_name: edit.invoice.customer_name, email: edit.invoice.email } : null
  );
  const [selectedContactAvatar, setSelectedContactAvatar] = useState<string | undefined>(() =>
    edit ? getDefaultAvatar(edit.invoice.customer_name || '?') : undefined
  );
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [allContacts, setAllContacts] = useState<ContactListItem[]>([]);
  const [isSearching, setIsSearching] = useState(true);
  // A quote keeps its own copy of the customer's address (Zoho fills it in when the quote is created). When a quote
  // has none — e.g. it was made before the contact had an address — the contact's addresses are offered here, like
  // Zoho's "Select address". Nothing is sent unless one is picked, so Zoho's own handling is never overridden.
  const [loadedAddresses, setLoadedAddresses] = useState<{ customerId: string; list: ContactAddress[] } | null>(null);
  const [quoteAddr, setQuoteAddr] = useState<QuoteAddress | null>(() =>
    edit?.invoice.billing_address?.address ? edit.invoice.billing_address as QuoteAddress : null
  );
  const [addressPicked, setAddressPicked] = useState(false);
  const pickerPortalRef = useRef<HTMLDivElement>(null);
  const customerFieldRef = useRef<HTMLIonItemElement>(null);
  const moreButtonRef = useRef<HTMLIonButtonElement>(null);
  const moreMenuCardRef = useRef<HTMLIonCardElement>(null);
  const closeCustomerPicker = useCallback(() => {
    setShowCustomerSearch(false);
    setCustomerSearch('');
  }, []);
  const customerResults = (() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return allContacts;
    return allContacts.filter(c => {
      const name = (c.displayName || `${c.firstName} ${c.lastName}`).toLowerCase();
      const email = (c.primaryEmail || '').toLowerCase();
      const phone = (c.primaryPhone || '').toLowerCase();
      return q.split(/\s+/).every(w => name.includes(w) || email.includes(w) || phone.includes(w));
    });
  })();
  const customerPickerOpen = showCustomerSearch;

  useEffect(() => {
    const svc = DataServiceFactory.getService();
    getCachedContactList(() => svc.contacts.getAllContacts())
      .then(result => {
        setAllContacts(result.data.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')));
        setIsSearching(false);
      })
      .catch(() => setIsSearching(false));
  }, []);

  // ── Line items state ──────────────────────────────────────────────────────
  const [lineItems, setLineItems] = useState<LineItem[]>(() =>
    edit ? zohoLineItemsToFormLines(edit.invoice.line_items) : [emptyLine()]
  );
  const [lineFormOpen, setLineFormOpen] = useState(false);
  const [lineFormData, setLineFormData] = useState<LineItem>(emptyLine());
  // The Item field opens a search sheet of the Zoho catalog; "Custom item" turns it into a text field for an item
  // that is not in the catalog
  const [itemSheetOpen, setItemSheetOpen] = useState(false);
  const [itemCustom, setItemCustom] = useState(false);
  const itemFieldRef = useRef<HTMLInputElement>(null);

  // ── Zoho catalog (item picker — used by all document types) ────────────────
  const [zohoItems, setZohoItems] = useState<ZohoItem[]>([]);
  const [zohoTaxes, setZohoTaxes] = useState<ZohoTax[]>([]);
  const [zohoPricelists, setZohoPricelists] = useState<ZohoPricelist[]>([]);
  // Zoho keeps the price list on each line (pricebook_id), not on the document.
  const [pricelistId, setPricelistId] = useState(() =>
    String(edit?.invoice.line_items?.find(l => l['pricebook_id'])?.['pricebook_id'] ?? '')
  );
  const [zohoLoading, setZohoLoading] = useState(false);
  const zohoFetchedRef = useRef(false);
  const zohoTaxesRef = useRef<ZohoTax[]>([]);
  // Item rates from the base catalog are pricelist-agnostic — when a price list is
  // selected, re-fetch items with that pricelist's actual rates so the item picker
  // (and the rate it fills in) reflects what the customer will really be charged.
  const [zohoPricelistItems, setZohoPricelistItems] = useState<ZohoItem[]>([]);
  // Zoho's pricebook-items endpoint returns id/name/rate only — no description/unit —
  // so a naive swap to the pricelist-specific list drops the item description shown
  // in the picker and saved onto the line. Keep the pricelist's rate but backfill the
  // description/unit from the base catalog by item_id.
  // Memoized: a fresh array every render would retrigger the render-phase
  // "prevEffectiveZohoItems" state adjustment below forever (infinite re-render).
  const effectiveZohoItems = useMemo(() => zohoPricelistItems.length > 0
    ? zohoPricelistItems.map(pItem => {
        const base = zohoItems.find(i => i.item_id === pItem.item_id);
        return { ...pItem, description: pItem.description ?? base?.description, unit: pItem.unit ?? base?.unit };
      })
    : zohoItems, [zohoPricelistItems, zohoItems]);

  // Clearing pricelist items when no pricelist is selected is computed
  // during render (React's "adjust state when a dependency changes"
  // pattern) rather than as a synchronous setState call at the top of the
  // effect, which now only handles the actual async fetch.
  // Set only when the user picks a price list on this form: the lines already added are then re-priced from it, as
  // Zoho's own header control does. Loading an existing document must not do this (it would overwrite saved rates).
  const repriceLinesForPricelistRef = useRef<string | null>(null);
  const repriceLines = useCallback((catalog: ZohoItem[]) => {
    setLineItems(prev => prev.map(l => {
      const item = l.item_id ? catalog.find(i => i.item_id === l.item_id) : undefined;
      return item && item.rate !== l.price ? { ...l, price: item.rate } : l;
    }));
  }, []);
  const changePricelist = (id: string) => {
    repriceLinesForPricelistRef.current = id;
    setPricelistId(id);
    if (!id) {
      // back to None: base catalog rates, which are already loaded
      repriceLinesForPricelistRef.current = null;
      repriceLines(zohoItems);
    }
  };
  const [prevPricelistId, setPrevPricelistId] = useState(pricelistId);
  if (pricelistId !== prevPricelistId) {
    setPrevPricelistId(pricelistId);
    if (!pricelistId) setZohoPricelistItems([]);
  }
  useEffect(() => {
    if (!pricelistId) return;
    let cancelled = false;
    fetchZohoPricelistItems(pricelistId)
      .then(items => {
        console.log(`[pricelist] ${pricelistId} → ${items.length} items, sample rate:`, items[0]?.rate);
        if (!cancelled) setZohoPricelistItems(items);
        if (!cancelled && repriceLinesForPricelistRef.current === pricelistId) {
          repriceLinesForPricelistRef.current = null;
          repriceLines(items);
        }
      })
      .catch(err => {
        console.error('[pricelist] fetchZohoPricelistItems failed:', err);
        if (!cancelled) setZohoPricelistItems([]);
      });
    return () => { cancelled = true; };
  }, [pricelistId, repriceLines]);

  // A line item already sitting in the open Add/Edit form was priced from whatever
  // catalog was active when it was picked — if the price list changes while that
  // form is still open, re-price it from the newly-fetched pricelist catalog instead
  // of leaving the stale rate behind. Computed during render (React's
  // "adjust state when a dependency changes" pattern) rather than as a
  // setState call inside an effect, since it's a pure re-derivation from
  // effectiveZohoItems with no external system involved.
  const [prevEffectiveZohoItems, setPrevEffectiveZohoItems] = useState(effectiveZohoItems);
  if (lineFormOpen && effectiveZohoItems !== prevEffectiveZohoItems) {
    setPrevEffectiveZohoItems(effectiveZohoItems);
    setLineFormData(prev => {
      if (!prev.item_id) return prev;
      const updated = effectiveZohoItems.find(i => i.item_id === prev.item_id);
      return (updated && updated.rate !== prev.price) ? { ...prev, price: updated.rate } : prev;
    });
  } else if (effectiveZohoItems !== prevEffectiveZohoItems) {
    setPrevEffectiveZohoItems(effectiveZohoItems);
  }

  // Fetch Zoho catalog once when Service Order type is active
  useEffect(() => {
    if (invoiceType !== 'ORDER' || zohoFetchedRef.current) return;
    zohoFetchedRef.current = true;
    setZohoLoading(true);
    fetchZohoAll()
      .then(all => {
        setZohoItems(all.items);
        setZohoTaxes(all.taxes);
        zohoTaxesRef.current = all.taxes;
        setZohoPricelists(all.pricelists ?? []);
        if (!comments) setComments(all.defaults?.notes ?? '');
        if (!terms) setTerms(all.defaults?.terms ?? '');
        const months = all.serviceMonthOptions.length ? all.serviceMonthOptions : [];
        setSoServiceMonths(months);
        soServiceMonthCfIdRef.current = all.serviceMonthCustomFieldId ?? '';
        const currentMonthName = new Date().toLocaleDateString('en-US', { month: 'long' }); // i18n-ignore: saved to Zoho, stays English
        setSoServiceMonth(months.find(m => m === currentMonthName) ?? months[0] ?? '');
      })
      .catch(() => {/* leave catalog empty if fetch fails */})
      .finally(() => setZohoLoading(false));
  }, [invoiceType, comments, terms, setComments]);

  // Fetch pricelists for invoice/quote types too (ORDER already gets them via fetchZohoAll)
  useEffect(() => {
    if (invoiceType === 'ORDER' || zohoPricelists.length > 0) return;
    fetchZohoPricelists().then(setZohoPricelists).catch(() => {});
  }, [invoiceType, zohoPricelists.length]);

  // Fetch the item catalog for INVOICE/QUOTE too — the item picker is the same
  // Zoho-catalog dropdown for every document type. ORDER gets its (richer) catalog
  // fetch via fetchZohoAll above; this is the lean equivalent for the other types.
  const zohoItemsLiteFetchedRef = useRef(false);
  useEffect(() => {
    if (invoiceType === 'ORDER' || zohoItemsLiteFetchedRef.current) return;
    zohoItemsLiteFetchedRef.current = true;
    fetchZohoItems().then(setZohoItems).catch(() => {});
    fetchZohoTaxes()
      .then(taxes => { setZohoTaxes(taxes); zohoTaxesRef.current = taxes; })
      .catch(() => {});
  }, [invoiceType]);

  // Fetch the Invoice module's "Service Month" custom field options once, when
  // INVOICE type is active — mirrors the Sales Order Service Month fetch above,
  // just against the "invoice" module instead of "salesorders".
  useEffect(() => {
    if (isServiceOrder || isQuote || invoiceServiceMonthsFetchedRef.current) return;
    invoiceServiceMonthsFetchedRef.current = true;
    setInvoiceServiceMonthsLoading(true);
    fetchZohoServiceMonths('invoice')
      .then(({ options, customFieldId }) => {
        setInvoiceServiceMonths(options);
        invoiceServiceMonthCfIdRef.current = customFieldId;
        const currentMonthName = new Date().toLocaleDateString('en-US', { month: 'long' }); // i18n-ignore: saved to Zoho, stays English
        setServiceMonth(prev => prev || options.find(m => m === currentMonthName) || options[0] || '');
      })
      .catch(() => {/* leave field empty if fetch fails */})
      .finally(() => setInvoiceServiceMonthsLoading(false));
  }, [isServiceOrder, isQuote]);

  const openLineForm = () => {
    setLineFormData(emptyLine());
    setLineFormOpen(true);
    setItemSheetOpen(false);
    setItemCustom(false);
  };

  const editLineForm = (l: LineItem) => {
    setLineFormData({ ...l });
    setLineFormOpen(true);
    setItemSheetOpen(false);
    // A line that did not come from the catalog is a custom item: its name is editable text
    setItemCustom(!l.item_id && !!l.description.trim());
  };

  // ── Modal visibility ──────────────────────────────────────────────────────
  const [sheet, setSheet] = useState<'moreMenu' | 'recurring' | null>(null);

  // Dismiss the More Options popover on any tap outside it or its trigger.
  useEffect(() => {
    if (sheet !== 'moreMenu') return;
    const handler = (e: PointerEvent) => {
      const t = e.target as Node;
      if (moreButtonRef.current?.contains(t)) return;
      if (moreMenuCardRef.current?.contains(t)) return;
      setSheet(null);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [sheet]);

  // ── Submission ────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAsDraft, setSavedAsDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<ZohoInvoice | null>(null);

  // "Save and Send" (Service Order) opens the email-compose screen right after
  // a successful create — flagged via this ref rather than a second submit
  // function so the header's plain "Save Draft" pill doesn't also trigger it.
  const emailAfterSubmitRef = useRef(false);
  const [showEmailCompose, setShowEmailCompose] = useState(false);

  // Apply an incoming prefill (e.g. from a Zoho Service Order) once on mount: seed line
  // items directly and open the search box pre-filled with a name so staff can confirm/
  // pick the match — the local contact list needs no debounce, filtering is synchronous.
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (!prefill || prefillAppliedRef.current) return;
    prefillAppliedRef.current = true;

    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body) — this genuinely runs once per mount, but the
    // effect-setState rule flags any setState reachable directly at an
    // effect's top level regardless of how many one-time guards gate it.
    Promise.resolve().then(() => {
      if (prefill.lineItems.length) {
        setLineItems(prefill.lineItems.map((li) => ({
          id: nextId(),
          description: li.description,
          itemDescription: li.itemDescription ?? '',
          quantity: li.quantity,
          price: li.price,
          discountAmount: li.discountAmount ?? '',
          discountType: '$',
          taxRate: li.taxRate ?? '',
        })));
      }
      if (prefill.customerName) {
        setShowCustomerSearch(true);
        setCustomerSearch(prefill.customerName);
      }
      if (prefill.linkedSalesOrderNumber) {
        setLinkedSalesOrderNumber(prefill.linkedSalesOrderNumber);
        // Also show it in the visible Reference # field so staff can see/
        // confirm the link before submitting — separate Zoho field from
        // salesorder_number (set via linkedSalesOrderNumber above), but no
        // reason not to show the same value in both.
        setReferenceNumber(prefill.linkedSalesOrderNumber);
      }
    });
  }, [prefill]);

  // Service Order: auto-populate pricelist / tax mode / a default line item from the
  // contact's billing config, mirroring CreateInvoiceDesktop.tsx's pickCustomer.
  const pickCustomer = useCallback(async (c: ContactListItem) => {
    setQuoteAddr(null); setAddressPicked(false); // the quote's old address belonged to the previous customer
    const name = c.displayName || `${c.firstName} ${c.lastName}`.trim();
    setSelectedCustomer({
      contact_id: c.zohoContactId ?? '',
      contact_name: name,
      email: c.primaryEmail,
      phone: c.primaryPhone,
    });
    setSelectedContactAvatar(proxyImage(c.avatar ?? '') || getDefaultAvatar(name));
    setCustomerSearch('');
    setShowCustomerSearch(false);

    // Billing-config autofill applies to service orders and invoices; quotes only take the price list.
    {
      try {
        const svc = DataServiceFactory.getService();
        const [contactSettled, billingSettled] = await Promise.allSettled([
          getCachedContact(c.id, () => svc.contacts.getContactById(c.id)),
          getDoc(doc(dbContacts, 'contactBilling', c.id)),
        ]);

        const contactResult = contactSettled.status === 'fulfilled' ? contactSettled.value : null;
        const billingSnap   = billingSettled.status === 'fulfilled' ? billingSettled.value : null;
        const billing = billingSnap?.exists() ? billingSnap.data() as Record<string, unknown> : null;

        const defaultTaxId = zohoTaxesRef.current.find(
          t => t.is_tax_group && Math.abs(t.tax_percentage - 10.55) < 0.01
        )?.tax_id;

        // The contact's billing doc stores a percentage and a tax-exempt flag; map it
        // to the matching Zoho tax (exempt = no tax).
        const invoiceTaxId = billing?.taxPreference === 'exempt'
          ? undefined
          : (typeof billing?.taxPercentage === 'number' && billing.taxPercentage > 0)
            ? findTaxIdByPercent(zohoTaxesRef.current, billing.taxPercentage)
            : undefined;

        if (billing?.defaultPricelistId) {
          setPricelistId(billing.defaultPricelistId as string);
        } else {
          const addresses = contactResult?.data?.addresses ?? [];
          const primary = addresses.find((a: { primary?: boolean }) => a.primary) ?? addresses[0];
          const zip = primary?.address?.postalCode;
          const code = zip ? zipToPricelistCode(zip) : null;
          const match = code ? zohoPricelists.find(p => p.name === code) : null;
          setPricelistId(match?.pricelist_id ?? '');
        }

        if (!isQuote && billing?.isInclusiveTax !== undefined) {
          setIsInclusiveTax(billing.isInclusiveTax as boolean);
        }

        if (!isQuote && billing?.defaultItemId) {
          // The invoice item catalog loads lazily — fetch it here if the pick beat it.
          const catalog = zohoItems.length ? zohoItems : (isServiceOrder ? [] : await fetchZohoItems().catch(() => []));
          const item = catalog.find(i => i.item_id === billing.defaultItemId);
          if (item) {
            // See ServiceOrderModal.tsx's matching comment / functions/src/services
            // /zohoServiceOrderService.ts's finalPriceToRate for the full reasoning:
            // billing.defaultRate is always "the final price the customer pays,"
            // not the Zoho line-item rate. Only back-calculate to a pre-tax rate
            // for Exclusive + Helcim-subscriber clients; everyone else uses it as-is.
            const defaultRate = billing.defaultRate as number | undefined;
            const isSubscriberExclusive = billing.isInclusiveTax === false && billing.isHelcimSubscriber === true;
            const taxPct = typeof billing.taxPercentage === 'number' ? billing.taxPercentage : 0;
            const rawPrice = defaultRate != null && defaultRate > 0 ? defaultRate : item.rate;
            const price = (isSubscriberExclusive && taxPct > 0 && defaultRate != null && defaultRate > 0)
              ? Math.round((rawPrice / (1 + taxPct / 100)) * 100) / 100
              : rawPrice;
            setLineItems([{
              id: nextId(),
              description: item.name,
              itemDescription: item.description ?? '',
              quantity: 1,
              price,
              discountAmount: '',
              discountType: '$',
              taxRate: '',
              item_id: item.item_id,
              tax_id: isServiceOrder ? defaultTaxId : invoiceTaxId,
            }]);
          }
        }
      } catch {
        // non-critical — silently skip if fetch fails
      }
    }
  }, [isServiceOrder, isQuote, zohoPricelists, zohoItems]);

  // Auto-select the customer once the real contact list has loaded, matched
  // by Zoho contact_id (not name — two different customers can share a
  // display name, which is exactly what went wrong matching by name here
  // before) — via the same pickCustomer() a manual search-and-click uses, so
  // it gets the contact's real avatar too. No ID or no match just leaves the
  // search box pre-filled with customerName (from the effect above) for a
  // manual pick — never guess/attach the wrong contact.
  const prefillContactMatchedRef = useRef(false);
  useEffect(() => {
    if (!prefill?.contactId || prefillContactMatchedRef.current || !allContacts.length) return;
    prefillContactMatchedRef.current = true;
    const match = allContacts.find(c => c.zohoContactId === prefill.contactId);
    if (match) Promise.resolve().then(() => pickCustomer(match));
  }, [allContacts, prefill, pickCustomer]);

  // The chosen customer's CURRENT addresses, read straight from Firestore (not the 5-minute contact cache, which
  // can hold an address-less copy).
  const quoteCustomerId = selectedCustomer?.contact_id;
  useEffect(() => {
    if (!isQuote || !quoteCustomerId) return;
    const match = allContacts.find(c => c.zohoContactId === quoteCustomerId);
    if (!match) return;
    let cancelled = false;
    DataServiceFactory.getService().contacts.getContactById(match.id).then(contact => {
      if (!cancelled) setLoadedAddresses({ customerId: quoteCustomerId, list: contact?.addresses ?? [] });
    }).catch(err => console.warn('[CreateInvoice] could not load the contact addresses:', err));
    return () => { cancelled = true; };
  }, [isQuote, quoteCustomerId, allContacts]);

  // Offer the picker only while the quote has no address and the contact has one to give. Once an address is
  // picked it stays, so the choice remains visible and changeable.
  const addressOptions = useMemo(
    () => (loadedAddresses && loadedAddresses.customerId === quoteCustomerId ? uniqueQuoteAddresses(loadedAddresses.list) : []),
    [loadedAddresses, quoteCustomerId]
  );
  const addressPickerShown = isQuote && !!selectedCustomer && addressOptions.length > 0 && (!quoteAddr || addressPicked);

  // ── Line item editing ─────────────────────────────────────────────────────
  const saveLineForm = (andNew: boolean) => {
    if (!lineFormData.description.trim()) return;
    setLineItems(prev => {
      const idx = prev.findIndex(l => l.id === lineFormData.id);
      if (idx === -1) return [...prev, lineFormData];
      return prev.map((l, i) => i === idx ? lineFormData : l);
    });
    if (andNew) {
      setLineFormData(emptyLine());
      setItemCustom(false);
    } else {
      setLineFormOpen(false);
    }
  };

  const removeLine = (id: number) =>
    setLineItems(prev => prev.filter(l => l.id !== id));

  // ── Computed totals ───────────────────────────────────────────────────────
  const getLineDiscountDollar = (l: LineItem) => {
    const amt = parseFloat(l.discountAmount) || 0;
    return l.discountType === '%' ? l.quantity * l.price * (amt / 100) : amt;
  };

  const subtotal = lineItems.reduce((s, l) => s + l.quantity * l.price, 0);
  const lineDisc = lineItems.reduce((s, l) => s + getLineDiscountDollar(l), 0);
  const lineTax = lineItems.reduce((s, l) => {
    const taxId = resolveLineTaxId(l, zohoTaxes);
    const r = taxId ? (zohoTaxes.find(t => t.tax_id === taxId)?.tax_percentage ?? 0) / 100 : 0;
    const net = l.quantity * l.price - getLineDiscountDollar(l);
    return s + (isInclusiveTax ? net - net / (1 + r) : net * r);
  }, 0);
  const total = isInclusiveTax ? subtotal - lineDisc : subtotal - lineDisc + lineTax;

  // Tax breakdown (Service Order only — uses Zoho tax group components)
  const taxBreakdownMap = (() => {
    if (!isServiceOrder) return new Map<string, { name: string; amount: number }>();
    const taxMap = new Map<string, { name: string; amount: number }>();
    for (const l of lineItems) {
      if (!l.tax_id) continue;
      const lineAmt = l.quantity * l.price;
      const discAmt = getLineDiscountDollar(l);
      const net = lineAmt - discAmt;
      const taxInfo = zohoTaxes.find(t => t.tax_id === l.tax_id);
      const groupPct = taxInfo?.tax_percentage ?? 0;
      const preTaxNet = isInclusiveTax
        ? (l.discountType === '$' && discAmt
            ? lineAmt / (1 + groupPct / 100) - discAmt
            : net / (1 + groupPct / 100))
        : net;
      if (taxInfo?.is_tax_group && taxInfo.taxes?.length) {
        for (const comp of taxInfo.taxes) {
          const amt = isInclusiveTax ? preTaxNet * (comp.tax_percentage / 100) : net * (comp.tax_percentage / 100);
          const ex = taxMap.get(comp.tax_id);
          if (ex) ex.amount += amt;
          else taxMap.set(comp.tax_id, { name: `${comp.tax_name} [${comp.tax_percentage}%]`, amount: amt });
        }
      } else {
        const pct = taxInfo?.tax_percentage ?? 0;
        const amt = isInclusiveTax ? preTaxNet * (pct / 100) : net * (pct / 100);
        const ex = taxMap.get(l.tax_id);
        if (ex) ex.amount += amt;
        else taxMap.set(l.tax_id, { name: taxInfo ? `${taxInfo.tax_name} [${taxInfo.tax_percentage}%]` : l.tax_id, amount: amt });
      }
    }
    return taxMap;
  })();
  const taxBreakdownRows = Array.from(taxBreakdownMap.values());
  const totalTaxAmt = taxBreakdownRows.reduce((s, r) => s + r.amount, 0);
  const adjustmentNum = parseFloat(adjustment) || 0;
  const grandTotal = isServiceOrder
    ? (isInclusiveTax ? subtotal + adjustmentNum : subtotal + totalTaxAmt + adjustmentNum)
    : total;

  // ── Payload builders ──────────────────────────────────────────────────────
  const buildInvoicePayload = () => {
    const numKey = isQuote ? 'estimate_number' : 'invoice_number';
    return {
      ...(selectedCustomer?.contact_id ? { customer_id: selectedCustomer.contact_id } : {}),
      ...(invoiceNumber.trim() && (isQuote || edit || invoiceNumberManual) ? { [numKey]: invoiceNumber.trim() } : {}),
      date: issuedOn,
      // Zoho estimates take expiry_date; due_date is the invoice field and is ignored on a quote.
      ...(isQuote ? { expiry_date: dueDate } : { due_date: dueDate }),
      currency_code: currency,
      is_inclusive_tax: isInclusiveTax,
      line_items: lineItems
        .filter(l => l.description.trim())
        .map(l => ({
          name: l.description.trim(),
          ...(l.itemDescription.trim() ? { description: l.itemDescription.trim() } : {}),
          rate: l.price,
          quantity: l.quantity,
          // Quotes keep the catalog item and the price list on each line, as Zoho's own quote form does.
          ...(isQuote && l.item_id ? { item_id: l.item_id } : {}),
          ...(isQuote && pricelistId ? { pricebook_id: pricelistId } : {}),
          ...(resolveLineTaxId(l, zohoTaxes) ? { tax_id: resolveLineTaxId(l, zohoTaxes) } : {}),
          ...(parseFloat(l.discountAmount) > 0 ? {
            discount: l.discountType === '%' ? `${parseFloat(l.discountAmount)}%` : parseFloat(l.discountAmount),
          } : {}),
        })),
      ...(comments.trim() ? { notes: comments.trim() } : {}),
      ...(isQuote && addressPicked && quoteAddr ? { billing_address: quoteAddr, shipping_address: quoteAddr } : {}),
      ...(referenceNumber.trim() ? { reference_number: referenceNumber.trim() } : {}),
      ...(linkedSalesOrderNumber ? { salesorder_number: linkedSalesOrderNumber } : {}),
      ...(serviceMonth ? {
        custom_fields: [{
          ...(invoiceServiceMonthCfIdRef.current
            ? { customfield_id: invoiceServiceMonthCfIdRef.current }
            : { label: 'Service Month' }),
          value: serviceMonth,
        }],
      } : {}),
    };
  };

  const buildServiceOrderPayload = () => {
    const termLabel = SO_PAYMENT_TERMS.find(t => t.days === paymentTermsDays)?.label ?? tr('pages.CreateInvoice.net3');
    return {
      ...(selectedCustomer?.contact_id ? { customer_id: selectedCustomer.contact_id } : {}),
      ...(invoiceNumber.trim() ? { salesorder_number: invoiceNumber.trim() } : {}),
      date: issuedOn,
      payment_terms: paymentTermsDays,
      payment_terms_label: termLabel,
      is_inclusive_tax: isInclusiveTax,
      ...(soServiceMonth ? {
        custom_fields: [{
          ...(soServiceMonthCfIdRef.current
            ? { customfield_id: soServiceMonthCfIdRef.current }
            : { label: 'Service Month' }),
          value: soServiceMonth,
        }],
      } : {}),
      ...(pricelistId ? { pricelist_id: pricelistId } : {}),
      line_items: lineItems
        .filter(l => l.description.trim())
        .map(l => ({
          name: l.description.trim(),
          ...(l.itemDescription.trim() ? { description: l.itemDescription.trim() } : {}),
          rate: l.price,
          quantity: l.quantity,
          ...(l.item_id ? { item_id: l.item_id } : {}),
          ...(l.tax_id ? { tax_id: l.tax_id } : {}),
          ...(parseFloat(l.discountAmount) > 0 ? {
            discount: l.discountType === '%' ? `${parseFloat(l.discountAmount)}%` : String(parseFloat(l.discountAmount)),
          } : {}),
        })),
      ...(lineItems.some(l => parseFloat(l.discountAmount) > 0) ? { discount_type: 'item_level' } : {}),
      ...(comments.trim() ? { customer_notes: comments.trim() } : {}),
      ...(terms.trim() ? { terms: terms.trim() } : {}),
      ...(adjustmentNum ? { adjustment: adjustmentNum, adjustment_description: adjustmentName.trim() || 'Adjustment' } : {}),
    };
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (draft: boolean) => {
    if (lineItems.length === 0 || lineItems.every(l => !l.description.trim())) {
      setError(tr('pages.CreateInvoice.addAtLeastOneLineItem')); return;
    }
    if (isServiceOrder && !selectedCustomer?.contact_id) {
      setError(tr('pages.CreateInvoice.selectACustomerToCreateA')); return;
    }
    setError(null);
    setSavedAsDraft(draft);
    if (draft) setIsSaving(true); else setIsSubmitting(true);
    try {
      // A Zoho quote takes its header Price List from the customer's contact (the line's pricebook_id alone
      // leaves it "None"), so put the chosen price list on the contact first — same write the contact
      // screens make. Non-fatal: the quote itself still saves.
      if (isQuote && pricelistId && selectedCustomer?.contact_id) {
        try {
          await updateZohoContact(selectedCustomer.contact_id, { pricebook_id: pricelistId });
        } catch (err) {
          console.warn('[CreateInvoice] could not set the contact price list:', err);
        }
      }
      const payload = isServiceOrder ? buildServiceOrderPayload() : buildInvoicePayload();
      const invoice = edit
        ? await updateInvoice(
            edit.isEstimate ? String((edit.invoice as { estimate_id?: string }).estimate_id ?? edit.invoice.invoice_id) : edit.invoice.invoice_id,
            payload,
            edit.isEstimate ? 'QUOTE' : undefined
          )
        : await createInvoice(payload, invoiceType);
      // A Service Order response only has salesorder_id/salesorder_number populated
      // (see the ZohoInvoice comment) — invoice_id would be undefined for it.
      const entityId = isServiceOrder ? (invoice.salesorder_id ?? invoice.invoice_id) : invoice.invoice_id;
      // Persist metadata
      try {
        await saveInvoiceMetadata(entityId, {
          dueDate: isServiceOrder ? issuedOn : dueDate,
          issuedOn,
          invoiceType,
          createdAt: new Date().toISOString(),
          ...((!isServiceOrder && serviceMonth) ? { serviceMonth } : {}),
          ...(isServiceOrder && soServiceMonth ? { serviceMonth: soServiceMonth } : {}),
        });
      } catch {
        // Non-fatal — invoice was created, metadata write is best-effort
      }
      // Complete the sales-order association (invoice already has its
      // salesorder_number field set from linkedSalesOrderNumber in the
      // payload above) — best-effort, same as the metadata save: the
      // invoice itself already exists either way.
      if (linkedSalesOrderNumber && !isServiceOrder && invoice.invoice_id) {
        try {
          console.log('[CreateInvoice] calling mapWithOrder with invoiceId:', JSON.stringify(invoice.invoice_id), 'linkedSalesOrderNumber:', JSON.stringify(linkedSalesOrderNumber));
          const mapResult = await mapInvoiceWithOrder(invoice.invoice_id);
          if (!mapResult.mapped) {
            console.warn('[CreateInvoice] mapWithOrder did not complete:', mapResult.message);
          }
        } catch (err) {
          console.error('[CreateInvoice] mapWithOrder failed:', err);
        }
      }
      setSuccess(invoice);
      onCreated?.(invoice);
      if (emailAfterSubmitRef.current) {
        emailAfterSubmitRef.current = false;
        setShowEmailCompose(true);
      }
    } catch (err) {
      setError(tr('pages.CreateInvoice.failed', { v1: err instanceof Error ? err.message : String(err) }));
    } finally {
      if (draft) setIsSaving(false); else setIsSubmitting(false);
    }
  };

  // ── Success: a toast, then the form closes (Save and send keeps it open for the email screen) ──
  useEffect(() => {
    if (!success || successAlertShownRef.current) return;
    successAlertShownRef.current = true;
    // A quote comes back from Zoho as an estimate: estimate_id / estimate_number, not the invoice fields.
    const savedDoc = success as ZohoInvoice & { estimate_id?: string; estimate_number?: string };
    const savedNumber = savedDoc.invoice_number ?? savedDoc.estimate_number ?? `#${savedDoc.invoice_id ?? savedDoc.estimate_id}`;
    const title = edit
      ? (edit.isEstimate ? tr('pages.CreateInvoice.quoteUpdated') : tr('pages.CreateInvoice.invoiceUpdated'))
      : savedAsDraft ? tr('pages.CreateInvoice.savedForLater') : tr('pages.CreateInvoice.invoiceCreated');
    void presentToast({
      message: `${title} ${savedNumber} ${fmt(grandTotal)}`,
      duration: 3500,
      position: 'bottom',
      cssClass: 'app-toast',
      icon: checkmarkCircle,
    });
    if (!showEmailCompose) onClose?.();
  }, [success, edit, savedAsDraft, grandTotal, showEmailCompose, presentToast, onClose, tr]);

  // ── Derived display values ─────────────────────────────────────────────────
  const customerName = selectedCustomer
    ? (selectedCustomer.contact_name ?? selectedCustomer.company_name ?? '')
    : null;
  const customerEmail = selectedCustomer?.email ?? null;
  const typeLabel = TYPE_OPTIONS.find(t => t.value === invoiceType)?.label ?? invoiceType;
  const detailsCardTitle = isQuote ? tr('pages.CreateInvoice.quoteDetails') : isServiceOrder ? tr('pages.CreateInvoice.serviceOrderDetails') : tr('pages.CreateInvoice.invoiceDetails');
  const numberLabel = isQuote ? tr('pages.CreateInvoice.quoteNumber') : isServiceOrder ? tr('pages.CreateInvoice.soNumber') : tr('pages.CreateInvoice.invoiceNumber');
  const lineFormIsEdit = lineItems.some(l => l.id === lineFormData.id && l.description.trim());

  const pageContent = (
    <>
      {/* Portal target for the customer-search results dropdown — see
          PortalPickerDropdown for why this needs to exist at all. */}
      <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
      <ItemPickerSheet
        isOpen={itemSheetOpen}
        onClose={() => setItemSheetOpen(false)}
        items={effectiveZohoItems}
        matches={itemMatchesQuery}
        formatRate={fmt}
        onSelect={(item) => {
          setLineFormData(prev => ({
            ...prev,
            description: item.name,
            itemDescription: item.description ?? '',
            price: item.rate,
            item_id: item.item_id,
            taxRate: '',
            tax_id: undefined,
          }));
          setItemCustom(false);
        }}
        onCustomItem={() => {
          setItemSheetOpen(false);
          setItemCustom(true);
          setTimeout(() => itemFieldRef.current?.focus(), 350); // after the sheet has slid away
        }}
      />
      <BasePageWithHeader
        title={edit ? (edit.isEstimate ? tr('pages.CreateInvoice.editQuote') : tr('pages.CreateInvoice.editInvoice')) : tr('pages.CreateInvoice.new', { typeLabel })}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        }
        endButtons={
          <>
            <IonButton fill="solid" className={styles.saveDraftHeaderBtn}
              disabled={isSaving || isSubmitting} onClick={() => handleSubmit(true)}>
              {isSaving
                ? <IonSpinner name="bubbles" color="light" style={{ width: 16, height: 16 }} />
                : edit ? tr('pages.CreateInvoice.save') : tr('pages.CreateInvoice.saveDraft')}
            </IonButton>
            <IonButton ref={moreButtonRef} shape="round" className={styles.moreButton} onClick={() => setSheet(s => s === 'moreMenu' ? null : 'moreMenu')}>
              <IonIcon slot="icon-only" icon={ellipsisVertical} />
            </IonButton>
          </>
        }
      >
      <IonContent scrollY={false} className={styles.mainContent}>
        <IonGrid className={styles.innerGrid}>
        {error && <IonLabel className={styles.errorBanner}>{error}</IonLabel>}

        {/* ── Document type — inline chips, like desktop's header switcher ───
            Hidden in edit mode: an existing invoice can't change document type. ── */}
        {!edit && (
        <IonLabel className={styles.typeChipRow}>
          {TYPE_OPTIONS.map(o => (
            <IonButton
              key={o.value}
              fill="clear"
              className={`${styles.typeChip} ${invoiceType === o.value ? styles.typeChipActive : ''}`}
              onClick={() => setInvoiceType(o.value)}
            >
              {o.label}
            </IonButton>
          ))}
        </IonLabel>
        )}

        {/* ── Customer card ──────────────────────────────────────────────── */}
        <IonCard className={styles.card}>
          <IonCardContent className={styles.cardBody}>
            <IonLabel className={styles.cardTitle}>
              <IonIcon icon={personOutline} />{tr('pages.CreateInvoice.customer')}
            </IonLabel>
            {selectedCustomer ? (
              <IonLabel className={styles.selectedCustomer}>
                <IonAvatar className={styles.customerAvatar}>
                  <img src={selectedContactAvatar} alt="" />
                </IonAvatar>
                <IonLabel className={styles.customerInfo}>
                  <IonLabel className={styles.customerName}>{customerName}</IonLabel>
                  {customerEmail && <IonLabel className={styles.customerEmail}>{customerEmail}</IonLabel>}
                </IonLabel>
                <IonButton fill="clear" className={styles.removeBtn}
                  onClick={() => { setSelectedCustomer(null); setSelectedContactAvatar(undefined); setQuoteAddr(null); setAddressPicked(false); }}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonLabel>
            ) : showCustomerSearch ? (
              <IonItem ref={customerFieldRef} lines="none" className={styles.searchBox}>
                <IonIcon icon={searchOutline} slot="start" className={styles.searchIcon} />
                <IonInput
                  className={styles.searchInput}
                  value={customerSearch}
                  onIonInput={e => setCustomerSearch(e.detail.value ?? '')}
                  placeholder={tr('pages.CreateInvoice.searchByNameEmailOrPhone')}
                  autofocus
                />
                <IonIcon icon={closeOutline} slot="end" className={styles.searchClear} onClick={closeCustomerPicker} />
                <PortalPickerDropdown
                  isOpen={customerPickerOpen}
                  fieldRef={customerFieldRef}
                  portalRef={pickerPortalRef}
                  close={closeCustomerPicker}
                  align="left"
                  matchWidth
                  exactWidth
                  className={styles.calendarDropdown}
                >
                  {isSearching ? (
                    <IonLabel className={styles.searchSpinnerWrap}>
                      <IonSpinner name="bubbles" color="primary" />
                    </IonLabel>
                  ) : customerResults.length > 0 ? (
                    <IonList lines="none" className={styles.dropdownList}>
                      {customerResults.slice(0, 40).map(c => {
                        const cName = c.displayName || `${c.firstName} ${c.lastName}`.trim();
                        return (
                          <IonItem key={c.id} button detail={false} lines="none" className={styles.dropdownItem}
                            onClick={() => { void pickCustomer(c); }}>
                            <IonAvatar slot="start" className={styles.customerResultAvatar}>
                              <img src={proxyImage(c.avatar ?? '') || getDefaultAvatar(cName || '?')} alt=""
                                onError={e => { (e.target as HTMLImageElement).src = getDefaultAvatar(cName || '?'); }} />
                            </IonAvatar>
                            <IonLabel>
                              <h3>{cName}</h3>
                              {c.primaryEmail && <p>{c.primaryEmail}</p>}
                            </IonLabel>
                          </IonItem>
                        );
                      })}
                    </IonList>
                  ) : (
                    <IonLabel className={styles.searchEmpty}>{tr('pages.CreateInvoice.noAccountsFound')}</IonLabel>
                  )}
                </PortalPickerDropdown>
              </IonItem>
            ) : (
              <IonButton fill="clear" className={styles.selectContactBtn} onClick={() => setShowCustomerSearch(true)}>
                <IonIcon icon={personOutline} />{tr('pages.CreateInvoice.selectCustomer')}
              </IonButton>
            )}
            {isQuote && selectedCustomer && addressPickerShown && (
              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.address')}</span>
                <SelectField
                  portalRef={pickerPortalRef}
                  value={quoteAddr ? String(addressOptions.findIndex(a => formatQuoteAddress(a) === formatQuoteAddress(quoteAddr))) : ''}
                  placeholder={tr('pages.CreateInvoice.selectAddress')}
                  options={addressOptions.map((a, i) => ({ value: String(i), label: formatQuoteAddress(a) }))}
                  onChange={v => { setQuoteAddr(addressOptions[Number(v)] ?? null); setAddressPicked(true); }}
                />
              </IonLabel>
            )}
          </IonCardContent>
        </IonCard>

        {/* ── Details card — fields directly editable, same as desktop ────── */}
        <IonCard className={styles.card}>
          <IonCardContent className={styles.cardBody}>
            <IonLabel className={styles.cardTitle}>
              <IonIcon icon={documentTextOutline} />
              {detailsCardTitle}
            </IonLabel>
            <IonLabel className={styles.formGrid}>
              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel}>{numberLabel}</span>
                <input className={styles.input} value={invoiceNumber}
                  onChange={e => { setInvoiceNumber(e.target.value); setInvoiceNumberManual(true); }} placeholder={tr('pages.CreateInvoice.autoGenerating')} />
              </IonLabel>

              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel}>{isQuote ? tr('pages.CreateInvoice.quoteDate') : isServiceOrder ? tr('pages.CreateInvoice.orderDate') : tr('pages.CreateInvoice.issuedDate')}</span>
                <DateField portalRef={pickerPortalRef} value={issuedOn} onChange={setIssuedOn} />
              </IonLabel>

              {!isServiceOrder && (
                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{isQuote ? tr('pages.CreateInvoice.expiryDate') : tr('pages.CreateInvoice.dueDate')}</span>
                  <DateField portalRef={pickerPortalRef} value={dueDate} onChange={setDueDate} />
                </IonLabel>
              )}

              {isServiceOrder && (
                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.paymentTerms')}</span>
                  <SelectField
                    portalRef={pickerPortalRef}
                    value={String(paymentTermsDays)}
                    options={SO_PAYMENT_TERMS.map(t => ({ value: String(t.days), label: t.label }))}
                    onChange={v => setPaymentTermsDays(Number(v))}
                  />
                </IonLabel>
              )}

              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.reference')}</span>
                <input className={styles.input} value={referenceNumber}
                  onChange={e => setReferenceNumber(e.target.value)} placeholder={tr('pages.CreateInvoice.optionalReference')} />
              </IonLabel>

              {!isQuote && !isServiceOrder && (
                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.serviceMonth')}</span>
                  <SelectField
                    portalRef={pickerPortalRef}
                    value={serviceMonth}
                    disabled={invoiceServiceMonthsLoading}
                    options={[{ value: '', label: tr('pages.CreateInvoice.none') }, ...invoiceServiceMonths.map(m => ({ value: m, label: m }))]}
                    onChange={setServiceMonth}
                  />
                </IonLabel>
              )}

              {isServiceOrder && (
                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.serviceMonth')}</span>
                  <SelectField
                    portalRef={pickerPortalRef}
                    value={soServiceMonth}
                    disabled={zohoLoading}
                    options={[{ value: '', label: tr('pages.CreateInvoice.none') }, ...soServiceMonths.map(m => ({ value: m, label: m }))]}
                    onChange={setSoServiceMonth}
                  />
                </IonLabel>
              )}

            </IonLabel>
          </IonCardContent>
        </IonCard>

        {/* ── Line items card ────────────────────────────────────────────── */}
        <IonCard className={styles.card}>
          <IonCardContent className={styles.cardBody}>
            <IonLabel className={styles.cardTitle}>
              <IonIcon icon={cubeOutline} />{tr('pages.CreateInvoice.lineItems')}
              {lineItems.some(l => l.description.trim()) && (
                <IonButton fill="clear" className={styles.clearAllBtn}
                  onClick={() => setLineItems([emptyLine()])}>{tr('pages.CreateInvoice.clearAll')}</IonButton>
              )}
            </IonLabel>

            {isServiceOrder && zohoLoading && (
              <IonLabel className={styles.loadingRow}>
                <IonSpinner name="bubbles" color="primary" style={{ width: 16, height: 16 }} />
                <span>{tr('pages.CreateInvoice.loadingCatalog')}</span>
              </IonLabel>
            )}

            <IonLabel className={styles.formGroup}>
              <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.tax')}</span>
              <IonRadioGroup
                value={isInclusiveTax ? 'inclusive' : 'exclusive'}
                onIonChange={e => setIsInclusiveTax(e.detail.value === 'inclusive')}
                className={styles.radioRow}
              >
                <IonRadio value="exclusive" className={styles.radioOption}>{tr('pages.CreateInvoice.exclusive')}</IonRadio>
                <IonRadio value="inclusive" className={styles.radioOption}>{tr('pages.CreateInvoice.inclusive')}</IonRadio>
              </IonRadioGroup>
            </IonLabel>

            {zohoPricelists.length > 0 && (
              <IonLabel className={styles.pricelistRow}>
                <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.priceList')}</span>
                <SelectField
                  portalRef={pickerPortalRef}
                  value={pricelistId}
                  options={[
                    { value: '', label: tr('pages.CreateInvoice.none') },
                    ...zohoPricelists.map(p => {
                      const { name, tip } = getPricelistInfo(p.name);
                      return { value: p.pricelist_id, label: name, description: tip || undefined };
                    }),
                  ]}
                  onChange={changePricelist}
                />
              </IonLabel>
            )}

            {lineItems.filter(l => l.description.trim()).map(l => (
              <IonLabel key={l.id} className={styles.lineItemRow}>
                <IonLabel className={styles.lineItemMain}>
                  <IonLabel>
                    <span className={styles.lineItemName}>{l.description}</span>
                    {isServiceOrder && l.itemDescription.trim() && (
                      <IonLabel className={styles.lineItemDesc}>{l.itemDescription}</IonLabel>
                    )}
                  </IonLabel>
                  <IonLabel style={{ display: 'flex', gap: 4 }}>
                    <IonButton fill="clear" className={styles.lineItemDelete} onClick={() => editLineForm(l)}>
                      <IonIcon icon={createOutline} />
                    </IonButton>
                    <IonButton fill="clear" className={styles.lineItemDelete} onClick={() => removeLine(l.id)}>
                      <IonIcon icon={trashOutline} />
                    </IonButton>
                  </IonLabel>
                </IonLabel>
                <IonLabel className={styles.lineItemSub}>
                  <span className={styles.lineItemQtyBadge}>{l.quantity}x</span>
                  <span className={styles.lineItemPrice}>{fmt(l.quantity * l.price)}</span>
                  {parseFloat(l.discountAmount) > 0 && (
                    <span className={styles.lineItemDiscount}>
                      -{l.discountType === '%' ? `${l.discountAmount}%` : fmt(parseFloat(l.discountAmount))}
                    </span>
                  )}
                  {resolveLineTaxId(l, zohoTaxes) && (
                    <span className={styles.lineItemTax}>
                      {zohoTaxes.find(t => t.tax_id === resolveLineTaxId(l, zohoTaxes))?.tax_name ?? ''}
                    </span>
                  )}
                </IonLabel>
              </IonLabel>
            ))}

            {lineFormOpen ? (
              <IonLabel className={styles.lineFormPanel}>
                <IonLabel className={styles.lineFormTitle}>{lineFormIsEdit ? tr('pages.CreateInvoice.editLineItem') : tr('pages.CreateInvoice.addLineItem')}</IonLabel>

                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.item')}</span>
                  <input
                    ref={itemFieldRef}
                    className={styles.input}
                    value={lineFormData.description}
                    readOnly={!itemCustom}
                    onClick={() => { if (!itemCustom) setItemSheetOpen(true); }}
                    // In picker mode the field only opens the list — it must not keep focus: a focused field behind the
                    // sheet makes the native webview scroll the whole page (and the sheet with it) to reveal it
                    onFocus={e => { if (!itemCustom) e.currentTarget.blur(); }}
                    // Custom mode is only for typing a name: leaving the field empty goes back to picking from the list
                    onBlur={() => { if (itemCustom && !lineFormData.description.trim()) setItemCustom(false); }}
                    onChange={e => setLineFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={tr('pages.CreateInvoice.searchItem')}
                  />
                </IonLabel>
                {lineFormData.itemDescription.trim() && (
                  <IonLabel className={styles.formGroup}>
                    <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.itemDescription')}</span>
                    <p className={styles.fieldHint}>{lineFormData.itemDescription}</p>
                  </IonLabel>
                )}

                <IonLabel className={styles.lineFormRow}>
                  <IonLabel className={styles.formGroup}>
                    <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.quantity')}</span>
                    <input type="number" className={styles.input} min={1}
                      value={lineFormData.quantity}
                      onChange={e => setLineFormData({ ...lineFormData, quantity: Number(e.target.value) || 1 })} />
                  </IonLabel>
                  <IonLabel className={styles.formGroup}>
                    <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.rate')}</span>
                    <input type="number" className={styles.input} step="0.01" min={0} placeholder="0"
                      value={lineFormData.price || ''}
                      onChange={e => setLineFormData({ ...lineFormData, price: parseFloat(e.target.value) || 0 })} />
                  </IonLabel>
                </IonLabel>

                <IonLabel className={styles.lineFormRow}>
                  <IonLabel className={styles.formGroup}>
                    <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.discount')}</span>
                    <input type="number" className={styles.input} step="0.01" min={0} placeholder="0"
                      value={lineFormData.discountAmount}
                      onChange={e => setLineFormData({ ...lineFormData, discountAmount: e.target.value })} />
                  </IonLabel>
                  <IonLabel className={styles.formGroup}>
                    <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.discountType')}</span>
                    <IonLabel className={styles.discountTypeRow}>
                      <label className={styles.radioLabel}>
                        <input type="radio" name={`dt-${lineFormData.id}`}
                          checked={lineFormData.discountType === '%'}
                          onChange={() => setLineFormData({ ...lineFormData, discountType: '%' })} />
                        %
                      </label>
                      <label className={styles.radioLabel}>
                        <input type="radio" name={`dt-${lineFormData.id}`}
                          checked={lineFormData.discountType === '$'}
                          onChange={() => setLineFormData({ ...lineFormData, discountType: '$' })} />
                        $
                      </label>
                    </IonLabel>
                  </IonLabel>
                </IonLabel>

                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('pages.CreateInvoice.tax')}</span>
                  <SelectField
                    portalRef={pickerPortalRef}
                    value={lineFormData.tax_id ?? ''}
                    options={[
                      { value: '', label: tr('pages.CreateInvoice.noTax') },
                      ...sortTaxesForPicker(zohoTaxes).map(t => ({
                        value: t.tax_id,
                        label: `${t.tax_name} (${t.tax_percentage}%)`,
                        group: t.is_tax_group ? tr('pages.CreateInvoice.taxGroups') : tr('pages.CreateInvoice.individualTaxes'),
                      })),
                    ]}
                    onChange={v => setLineFormData({ ...lineFormData, tax_id: v || undefined, taxRate: '' })}
                  />
                </IonLabel>

                <IonLabel className={styles.lineFormActions}>
                  <IonButton fill="clear" className={styles.lineFormCancelBtn} onClick={() => setLineFormOpen(false)}>{tr('pages.CreateInvoice.cancel')}
                  </IonButton>
                  <IonButton fill="clear" className={styles.lineFormSaveNewBtn}
                    disabled={!lineFormData.description.trim()} onClick={() => saveLineForm(true)}>{tr('pages.CreateInvoice.saveNew')}
                  </IonButton>
                  <IonButton fill="clear" className={styles.lineFormSaveBtn}
                    disabled={!lineFormData.description.trim()} onClick={() => saveLineForm(false)}>{tr('pages.CreateInvoice.save')}
                  </IonButton>
                </IonLabel>
              </IonLabel>
            ) : (
              <IonButton fill="clear" className={styles.addItemBtn} onClick={openLineForm}>
                <IonIcon icon={cubeOutline} />{tr('pages.CreateInvoice.addLineItem')}
              </IonButton>
            )}
          </IonCardContent>
        </IonCard>

        {/* ── Billing summary ────────────────────────────────────────────── */}
        <IonCard className={styles.card}>
          <IonCardContent className={styles.cardBody}>
            <IonLabel className={styles.cardTitle}>{tr('pages.CreateInvoice.billingSummary')}</IonLabel>
            <IonLabel className={styles.billingRow}>
              <span>{tr('pages.CreateInvoice.subtotal')}{isInclusiveTax ? tr('pages.CreateInvoice.taxIncl') : ''}</span>
              <span>{fmt(subtotal)}</span>
            </IonLabel>
            {lineDisc > 0 && (
              <IonLabel className={styles.billingRow}>
                <span>{tr('pages.CreateInvoice.discount')}</span>
                <span className={styles.billingValue}>-{fmt(lineDisc)}</span>
              </IonLabel>
            )}
            {isServiceOrder && taxBreakdownRows.length > 0 && (
              <>
                {taxBreakdownRows.map(r => (
                  <IonLabel key={r.name} className={styles.billingRow}>
                    <span>{r.name}</span><span>{fmt(r.amount)}</span>
                  </IonLabel>
                ))}
                <IonLabel className={styles.billingRow}>
                  <span>{tr('pages.CreateInvoice.totalTax')}</span><span style={{ fontWeight: 700 }}>{fmt(totalTaxAmt)}</span>
                </IonLabel>
              </>
            )}
            {!isServiceOrder && lineTax > 0 && (
              <IonLabel className={styles.billingRow}>
                <span>{tr('pages.CreateInvoice.tax')}</span><span>{fmt(lineTax)}</span>
              </IonLabel>
            )}
            {isServiceOrder && (
              <IonLabel className={`${styles.billingRow} ${styles.adjustmentRow}`}>
                <input type="text" className={styles.adjustmentNameInput} value={adjustmentName}
                  onChange={e => setAdjustmentName(e.target.value)} placeholder={tr('pages.CreateInvoice.adjustment')} />
                <input type="text" inputMode="decimal" className={styles.adjustmentInput} value={adjustment}
                  placeholder="0.00" onChange={e => setAdjustment(e.target.value)}
                  onBlur={e => { const n = parseFloat(e.target.value); setAdjustment(isNaN(n) ? '' : String(n)); }} />
              </IonLabel>
            )}
            <IonLabel className={`${styles.billingRow} ${styles.billingTotal}`}>
              <span>{tr('pages.CreateInvoice.total')}{isServiceOrder ? tr('pages.CreateInvoice.usd') : ` (${currency})`}</span>
              <span>{fmt(grandTotal)}</span>
            </IonLabel>
          </IonCardContent>
        </IonCard>

        {/* ── Comments ──────────────────────────────────────────────────── */}
        <IonCard className={styles.card}>
          <IonCardContent className={styles.cardBody}>
            <IonLabel className={styles.cardTitle}>{tr('pages.CreateInvoice.notesComments')}</IonLabel>
            <textarea className={styles.textarea} value={comments}
              onChange={e => setComments(e.target.value)} placeholder={tr('pages.CreateInvoice.includeAnyAdditionalDetails')} rows={4} />
          </IonCardContent>
        </IonCard>

        {/* ── Terms & Conditions (Service Order only) ──────────────────── */}
        {isServiceOrder && (
          <IonCard className={styles.card}>
            <IonCardContent className={styles.cardBody}>
              <IonLabel className={styles.cardTitle}>{tr('pages.CreateInvoice.termsConditions')}</IonLabel>
              <textarea className={styles.textarea} value={terms}
                onChange={e => setTerms(e.target.value)} placeholder={tr('pages.CreateInvoice.paymentPolicyCancellationTerms')} rows={4} />
            </IonCardContent>
          </IonCard>
        )}

        {/* ── More Options popover — same glass-panel dropdown chrome as every
            other picker in this form. Fixed-positioned under the ⋮ button rather
            than routed through the shared ref-based align="right" math, which
            was consistently landing far to the left of the trigger for this one
            case instead of hugging its right edge. ─────────────────────────── */}
        {sheet === 'moreMenu' && (
          <IonCard ref={moreMenuCardRef} className={`${styles.calendarDropdown} ${styles.moreMenuPopover}`}>
          <IonList lines="none" className={styles.dropdownList}>
            {isServiceOrder ? (
              // Matches Zoho's own mobile app — a Sales Order's overflow menu is just this.
              <IonItem button detail={false} lines="none" className={styles.dropdownItem}
                disabled={isSubmitting || isSaving}
                onClick={() => { setSheet(null); emailAfterSubmitRef.current = true; handleSubmit(false); }}>
                <IonLabel>{isSubmitting ? tr('pages.CreateInvoice.saving') : tr('pages.CreateInvoice.saveAndSend')}</IonLabel>
              </IonItem>
            ) : (
              <>
                <IonItem button detail={false} lines="none" className={styles.dropdownItem}
                  disabled={isSubmitting || isSaving}
                  onClick={() => { setSheet(null); handleSubmit(false); }}>
                  <IonIcon icon={checkmarkOutline} slot="start" className={styles.moreMenuIcon} />
                  <IonLabel>{isSubmitting ? tr('pages.CreateInvoice.saving') : edit ? tr('pages.CreateInvoice.saveChanges') : tr('pages.CreateInvoice.create', { typeLabel })}</IonLabel>
                </IonItem>
                <IonItem button detail={false} lines="none" className={styles.dropdownItem}
                  onClick={() => {
                    setSheet(null);
                    if (navigator.share) {
                      navigator.share({
                        title: `New ${typeLabel}`,
                        text: `Invoice from RS Maids`,
                        url: window.location.href,
                      }).catch(() => {});
                    }
                  }}>
                  <IonIcon icon={shareOutline} slot="start" className={styles.moreMenuIcon} />
                  <IonLabel>{tr('pages.CreateInvoice.shareLink')}</IonLabel>
                </IonItem>
                <IonItem button detail={false} lines="none" className={styles.dropdownItem}
                  onClick={() => { setSheet(null); window.print(); }}>
                  <IonIcon icon={documentOutline} slot="start" className={styles.moreMenuIcon} />
                  <IonLabel>{tr('pages.CreateInvoice.downloadPrintPdf')}</IonLabel>
                </IonItem>
                <IonItem button detail={false} lines="none" className={styles.dropdownItem}
                  onClick={() => {
                    setSheet(null);
                    // TODO: send pay-now email via Helcim/email service
                  }}>
                  <IonIcon icon={mailOutline} slot="start" className={styles.moreMenuIcon} />
                  <IonLabel>{tr('pages.CreateInvoice.sendPayNowEmail')}</IonLabel>
                </IonItem>
                <IonItem button detail={false} lines="none" className={styles.dropdownItem}
                  onClick={() => {
                    setSheet(null);
                    // TODO: send with deposit request
                  }}>
                  <IonIcon icon={cashOutline} slot="start" className={styles.moreMenuIcon} />
                  <IonLabel>{tr('pages.CreateInvoice.sendWithDepositRequest')}</IonLabel>
                </IonItem>
                {invoiceType === 'INVOICE' && (
                  <IonItem button detail={false} lines="none" className={styles.dropdownItem}
                    onClick={() => {
                      setRecurringError(null);
                      setRecurringSuccess(false);
                      setSheet('recurring');
                    }}>
                    <IonIcon icon={reloadOutline} slot="start" className={styles.moreMenuIcon} />
                    <IonLabel>{tr('pages.CreateInvoice.makeInvoiceRecurring')}</IonLabel>
                  </IonItem>
                )}
              </>
            )}
          </IonList>
          </IonCard>
        )}

        {/* ── Recurring Billing Sheet ──────────────────────────────────── */}
        {sheet === 'recurring' && (
          <Sheet title={tr('pages.CreateInvoice.makeInvoiceRecurring')} onClose={() => setSheet(null)}>
            {recurringSuccess ? (
              <IonLabel className={styles.sheetSection} style={{ textAlign: 'center', padding: '24px 0' }}>
                <IonLabel style={{ fontSize: 48 }}>✅</IonLabel>
                <IonLabel style={{ fontWeight: 600, marginTop: 12 }}>{tr('pages.CreateInvoice.recurringBillingSetUp')}</IonLabel>
                <IonLabel style={{ color: 'var(--ion-color-medium)', marginTop: 8, fontSize: 14 }}>{tr('pages.CreateInvoice.aRecurringInvoiceHasBeenSet')}
                </IonLabel>
                <IonButton expand="block" className={styles.sheetSaveBtn} style={{ marginTop: 24 }}
                  onClick={() => setSheet(null)}>{tr('pages.CreateInvoice.done')}</IonButton>
              </IonLabel>
            ) : (
              <>
                {recurringError && (
                  <IonLabel className={styles.sheetSection} style={{ color: 'var(--ion-color-danger)', fontSize: 13 }}>
                    {recurringError}
                  </IonLabel>
                )}

                {/* No customer warning */}
                {!selectedCustomer && (
                  <IonLabel className={styles.sheetSection} style={{ color: 'var(--ion-color-warning)', fontSize: 13 }}>{tr('pages.CreateInvoice.aCustomerWithASavedPayment')}
                  </IonLabel>
                )}

                <IonLabel className={styles.sheetSection}>
                  <IonLabel className={styles.sheetSectionTitle}>{tr('pages.CreateInvoice.billingPeriod')}</IonLabel>
                  {(['daily', 'weekly', 'monthly', 'yearly'] as const).map(p => (
                    <IonButton key={p} fill="clear"
                      className={`${styles.typeMenuOption} ${recurBillingPeriod === p ? styles.typeMenuOptionActive : ''}`}
                      onClick={() => setRecurBillingPeriod(p)}
                    >
                      <span className={styles.typeMenuLabel}>{p.charAt(0).toUpperCase() + p.slice(1)}</span>
                      {recurBillingPeriod === p && <IonIcon icon={checkmarkOutline} className={styles.typeMenuCheck} />}
                    </IonButton>
                  ))}
                </IonLabel>

                <IonLabel className={styles.sheetSection}>
                  <IonLabel className={styles.sheetSectionTitle}>{tr('pages.CreateInvoice.repeatEvery')}</IonLabel>
                  <input
                    type="number" min={1} max={99}
                    className={styles.recurringInput}
                    value={recurIncrements}
                    onChange={e => setRecurIncrements(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                  <IonLabel style={{ color: 'var(--ion-color-medium)', fontSize: 12, marginTop: 4 }}>{tr('pages.CreateInvoice.eG2MonthlyEvery2')}
                  </IonLabel>
                </IonLabel>

                <IonLabel className={styles.sheetSection}>
                  <IonLabel className={styles.sheetSectionTitle}>{tr('pages.CreateInvoice.cycles0Infinite')}</IonLabel>
                  <input
                    type="number" min={0}
                    className={styles.recurringInput}
                    value={recurCycles}
                    onChange={e => setRecurCycles(Math.max(0, parseInt(e.target.value) || 0))}
                  />
                </IonLabel>

                <IonLabel className={styles.sheetSection}>
                  <IonLabel className={styles.sheetSectionTitle}>{tr('pages.CreateInvoice.startDate')}</IonLabel>
                  <DateField portalRef={pickerPortalRef} value={recurStartDate} onChange={setRecurStartDate} />
                </IonLabel>

                <IonButton
                  expand="block"
                  className={styles.sheetSaveBtn}
                  disabled={!selectedCustomer || isSubmittingRecurring}
                  onClick={async () => {
                    if (!selectedCustomer?.contact_id) {
                      setRecurringError(tr('pages.CreateInvoice.noCustomerSelectedPleaseAddA'));
                      return;
                    }
                    setRecurringError(null);
                    setIsSubmittingRecurring(true);
                    try {
                      await makeZohoInvoiceRecurring({
                        customerId: selectedCustomer.contact_id,
                        recurrenceName: `RS Maids — ${invoiceNumber || 'service'} (${recurBillingPeriod})`,
                        billingPeriod: recurBillingPeriod,
                        billingPeriodIncrements: recurIncrements,
                        startDate: recurStartDate,
                        maxCycles: recurCycles,
                        currency,
                        lineItems: lineItems.filter(l => l.description.trim()).map(l => ({ description: l.description.trim(), rate: l.price, quantity: l.quantity })),
                      });
                      setRecurringSuccess(true);
                    } catch (err) {
                      setRecurringError(err instanceof Error ? err.message : String(err));
                    } finally {
                      setIsSubmittingRecurring(false);
                    }
                  }}
                >
                  {isSubmittingRecurring ? <IonSpinner name="bubbles" color="primary" /> : tr('pages.CreateInvoice.setUpRecurringBilling')}
                </IonButton>
              </>
            )}
          </Sheet>
        )}
        </IonGrid>
      </IonContent>
      </BasePageWithHeader>

      {success && (
        <IonModal
          isOpen={showEmailCompose}
          onDidDismiss={() => setShowEmailCompose(false)}
          onWillDismiss={() => setShowEmailCompose(false)}
          canDismiss={true}
          backdropDismiss={true}
          keepContentsMounted={false}
          handle={false}
          className={styles.fullscreenModal}
        >
          <EmailCompose
            entityType="salesorder"
            entityId={success.salesorder_id ?? success.invoice_id}
            entityNumber={success.salesorder_number ?? success.invoice_number}
            defaultTo={success.email ?? selectedCustomer?.email}
            onClose={() => { setShowEmailCompose(false); onClose?.(); }}
          />
        </IonModal>
      )}
    </>
  );

  return onClose ? <IonLabel className={styles.modalWrapper}>{pageContent}</IonLabel> : <IonPage className={styles.page}>{pageContent}</IonPage>;
};

export default CreateInvoice;
